/**
 * crypto.ts — Application-Layer-Field-Encryption (XSalsa20-Poly1305)
 *
 * Zweck:
 *   Sensible Felder (BWA-Roh-Payload, Gesundheitsfaktoren der Dimension 5,
 *   private Lebensumstände der Dimension 6) werden vor der Persistenz in
 *   Supabase zusätzlich auf Anwendungsebene verschlüsselt. Postgres-at-rest
 *   reicht laut DSFA NICHT für Art-9-Daten kombiniert mit Sozialdaten nach
 *   § 35 SGB I — Defense-in-Depth GREIFT VOR der RLS-Policy.
 *
 * Implementierung (AP11b, 2026-05-14):
 *   - libsodium-wrappers-sumo (BSD-2-Clause), volle libsodium-API
 *   - crypto_secretbox_easy (XSalsa20 + Poly1305) für authenticated encryption
 *   - 24-byte Nonce wird pro Aufruf zufällig erzeugt und an den Ciphertext
 *     geprependet — Output-Format: nonce(24) || cipher(MAC(16) + payload(N))
 *   - Per-User-Key-Derivation: master(32 byte) + KDF + userId → user-key(32 byte)
 *     via crypto_kdf_derive_from_key. Master-Key kommt aus VITE_CRYPTO_MASTER_KEY
 *     (siehe README + apps/web/.env.example). Niemals ins Git, niemals geloggt.
 *
 * Key-Strategie (Status 2026-05-14):
 *   Phase 1 (jetzt):   ENV-injizierter Master-Key + KDF pro userId.
 *                      Pragmatisch für den Pilot; rotation = neuer ENV-Wert
 *                      → bestehende Ciphertexte werden re-encrypted via Migration.
 *   Phase 2 (später):  Master-Key aus Supabase Vault über authenticated
 *                      Edge-Function. Aufrufer-Vertrag (encryptField/decryptField)
 *                      bleibt stabil — nur loadMasterKey() wechselt seine Quelle.
 *
 * Aufrufer-Vertrag:
 *   1. encryptField(plaintext, userId): Promise<Uint8Array>
 *      Liefert nonce||cipher als Uint8Array (passt zu bytea-Spalten ohne
 *      doppelte base64-Kodierung; Supabase serialisiert Uint8Array selbst).
 *   2. decryptField(ciphertext, userId): Promise<string>
 *      Wirft bei MAC-Fehlschlag (Manipulation oder falscher Key).
 *   3. isEncryptedAvailable(): boolean
 *      true sobald libsodium initialisiert UND Master-Key geladen ist.
 *      UI MUSS hard-blocken solange false (kein Klartext-Fallback!).
 *
 * @see /Users/christiankopsch/Projects/Tragfaehigkeit_App/CLAUDE.md
 *      (DSGVO-Regel 2: Verschlüsselung)
 */

// sumo-Variante notwendig für crypto_pwhash / crypto_kdf — nicht im "small"-Build.
import sodium from 'libsodium-wrappers-sumo';

// -----------------------------------------------------------------------------
// Konstanten — explizit, damit Reviewer sie nachvollziehen können.
// crypto_secretbox: 24-byte nonce, 32-byte key, 16-byte MAC.
// crypto_kdf:        32-byte master, 8-char ASCII context, 64-bit subkey-id.
// -----------------------------------------------------------------------------
const NONCE_BYTES = 24;
const KEY_BYTES = 32;
/** 8 ASCII chars exakt — libsodium-Konvention für crypto_kdf-Context. */
const KDF_CONTEXT = 'TRGFKEIT';

// -----------------------------------------------------------------------------
// Modul-State (lebt im RAM, niemals persistiert).
// -----------------------------------------------------------------------------
let initPromise: Promise<void> | null = null;
let masterKey: Uint8Array | null = null;
/** Cache abgeleiteter User-Keys. Niemals serialisieren. */
const userKeyCache = new Map<string, Uint8Array>();

// -----------------------------------------------------------------------------
// libsodium ready — async WASM-Init, idempotent gecached.
// -----------------------------------------------------------------------------
async function ensureSodiumReady(): Promise<void> {
  if (!initPromise) {
    initPromise = sodium.ready;
  }
  await initPromise;
}

/**
 * Lädt den Master-Key aus VITE_CRYPTO_MASTER_KEY (base64, 32 Byte raw).
 * Idempotent: einmal geladen, im RAM gehalten.
 *
 * Die Funktion ist NICHT exportiert — Aufrufer triggern Init implizit über
 * encryptField/decryptField. Tests mit injectMasterKeyForTest() überschreiben
 * den Pfad bewusst.
 *
 * @throws Error wenn ENV-Var fehlt, falsches Format, oder Key-Länge ≠ 32 Byte.
 */
async function ensureMasterKeyLoaded(): Promise<void> {
  await ensureSodiumReady();
  if (masterKey !== null) return;

  // Vite injiziert import.meta.env zur Build-Zeit. In Tests (Node) liefert
  // import.meta.env ein Stub-Object — wir lesen defensiv mit unknown-Cast.
  const env = (typeof import.meta !== 'undefined' ? import.meta.env : undefined) as
    | Record<string, string | undefined>
    | undefined;
  const raw = env?.VITE_CRYPTO_MASTER_KEY;

  if (typeof raw !== 'string' || raw.length === 0) {
    throw new Error(
      'crypto: VITE_CRYPTO_MASTER_KEY fehlt. ' +
        'Generieren mit: pnpm exec node -e "console.log(crypto.randomBytes(32).toString(\'base64\'))" — ' +
        'in apps/web/.env.local und Cloudflare-Pages-Env-Vars eintragen.',
    );
  }

  let decoded: Uint8Array;
  try {
    decoded = sodium.from_base64(raw, sodium.base64_variants.ORIGINAL);
  } catch {
    throw new Error('crypto: VITE_CRYPTO_MASTER_KEY ist kein gültiges base64.');
  }
  if (decoded.length !== KEY_BYTES) {
    throw new Error(
      `crypto: VITE_CRYPTO_MASTER_KEY muss exakt ${String(KEY_BYTES)} Byte (44 base64-Zeichen) lang sein, war ${String(decoded.length)} Byte.`,
    );
  }
  // Defensive Kopie, damit kein externer Code den Buffer überschreiben kann.
  masterKey = new Uint8Array(decoded);
}

/**
 * Test-only: injiziert einen rohen 32-Byte Master-Key, ohne ENV-Lookup.
 * Nicht im Production-Bundle aufrufen.
 */
export async function injectMasterKeyForTest(rawKey: Uint8Array): Promise<void> {
  await ensureSodiumReady();
  if (rawKey.length !== KEY_BYTES) {
    throw new Error(`injectMasterKeyForTest: key muss exakt ${String(KEY_BYTES)} Byte sein.`);
  }
  masterKey = new Uint8Array(rawKey);
  userKeyCache.clear();
}

/**
 * Test-only / Logout: vergisst Master-Key UND alle abgeleiteten User-Keys.
 * Best-effort: überschreibt Buffer vor dem Verwerfen.
 */
export function clearCryptoState(): void {
  if (masterKey) masterKey.fill(0);
  masterKey = null;
  for (const k of userKeyCache.values()) k.fill(0);
  userKeyCache.clear();
}

/**
 * Leitet einen User-spezifischen 32-Byte Key aus dem Master + userId ab.
 *
 * Wir nutzen crypto_kdf_derive_from_key (BLAKE2b-basiert, libsodium-Standard
 * für KDF). Die subkey_id wird aus den ersten 8 Byte eines BLAKE2b-Hashes
 * der userId gebildet — deterministisch, kollisionsresistent für UUIDs und
 * jede halbwegs vernünftige ID-Quelle.
 *
 * Cache pro userId, damit pro Roundtrip nur ein KDF-Call anfällt.
 */
function deriveUserKey(userId: string): Uint8Array {
  if (masterKey === null) {
    throw new Error(
      'deriveUserKey: master key not loaded — internal bug, call ensureMasterKeyLoaded first',
    );
  }
  const cached = userKeyCache.get(userId);
  if (cached) return cached;

  if (typeof userId !== 'string' || userId.length === 0) {
    throw new Error('crypto.deriveUserKey: userId muss ein nicht-leerer String sein.');
  }

  // 8 Byte Hash der userId → als Little-Endian uint64 (bigint) für die
  // subkey_id. libsodium akzeptiert bigint direkt — keine 53-bit-Falle.
  const hash = sodium.crypto_generichash(8, userId, null);
  let subkeyId = 0n;
  for (let i = 0; i < 8; i++) {
    subkeyId |= BigInt(hash[i] ?? 0) << BigInt(i * 8);
  }

  const userKey = sodium.crypto_kdf_derive_from_key(KEY_BYTES, subkeyId, KDF_CONTEXT, masterKey);
  userKeyCache.set(userId, userKey);
  return userKey;
}

// -----------------------------------------------------------------------------
// Public API — siehe Aufrufer-Vertrag im Dateikopf.
// -----------------------------------------------------------------------------

/**
 * Verschlüsselt einen Klartext-String mit dem User-Key.
 *
 * @param plaintext UTF-8-String beliebiger Länge (auch leer).
 * @param userId    Stabiler User-Identifier (z.B. auth.users.id).
 * @returns nonce(24) || cipher(MAC(16) + payload(N)) als Uint8Array.
 */
export async function encryptField(plaintext: string, userId: string): Promise<Uint8Array> {
  await ensureMasterKeyLoaded();
  const key = deriveUserKey(userId);
  const nonce = sodium.randombytes_buf(NONCE_BYTES);
  const cipher = sodium.crypto_secretbox_easy(sodium.from_string(plaintext), nonce, key);
  const combined = new Uint8Array(nonce.length + cipher.length);
  combined.set(nonce, 0);
  combined.set(cipher, nonce.length);
  return combined;
}

/**
 * Entschlüsselt nonce||cipher zu Klartext-String mit dem User-Key.
 *
 * @throws Error wenn libsodium/Master-Key nicht initialisiert,
 *         Ciphertext zu kurz, oder MAC-Fehlschlag (Manipulation / falscher Key).
 */
export async function decryptField(ciphertext: Uint8Array, userId: string): Promise<string> {
  await ensureMasterKeyLoaded();
  const key = deriveUserKey(userId);

  // Vertrag: ciphertext ist Uint8Array. JS-Aufrufer könnten zur Laufzeit
  // andere Buffer-Typen reichen — wir verlassen uns auf den TS-Typ und werfen
  // sauber bei Längencheck, falls ein fremder Typ doch durchrutscht.
  if (ciphertext.length < NONCE_BYTES + sodium.crypto_secretbox_MACBYTES) {
    throw new Error('crypto.decryptField: ciphertext too short (truncated or corrupted)');
  }
  const nonce = ciphertext.slice(0, NONCE_BYTES);
  const cipher = ciphertext.slice(NONCE_BYTES);
  let plain: Uint8Array;
  try {
    plain = sodium.crypto_secretbox_open_easy(cipher, nonce, key);
  } catch {
    throw new Error('crypto.decryptField: authentication failed (data tampered or wrong key)');
  }
  return sodium.to_string(plain);
}

/**
 * Synchroner Liveness-Check: liefert `true` sobald libsodium initialisiert UND
 * der Master-Key im RAM steht. UI MUSS dies vor jedem sensiblen Eingabefeld
 * prüfen — bei `false` gibt es KEIN Klartext-Fallback.
 *
 * Vor dem ersten encrypt/decrypt-Call kann das `false` liefern, selbst wenn
 * die ENV-Var korrekt gesetzt ist (lazy init). Aufrufer können stattdessen
 * `await ensureCryptoReady()` rufen, um den Init zu erzwingen.
 */
export function isEncryptedAvailable(): boolean {
  if (masterKey === null) return false;
  try {
    return typeof sodium.crypto_secretbox_easy === 'function';
  } catch {
    return false;
  }
}

/**
 * Erzwingt Crypto-Init (libsodium-WASM + Master-Key-Load). Sollte beim
 * App-Bootstrap gerufen werden, damit `isEncryptedAvailable()` ab dem ersten
 * Render zuverlässig `true` liefert. Idempotent.
 *
 * @throws Error wenn ENV-Var fehlt oder ungültig — App MUSS in dem Fall
 *         hard-blocken (sensible Eingaben verbieten).
 */
export async function ensureCryptoReady(): Promise<void> {
  await ensureMasterKeyLoaded();
}
